import React, { useCallback } from "react";
import "../styles/sidePanel.css";
import { useAuth } from "../contexts/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";

type NavItem = {
  id: string;
  label: string;
  workspaceId: string;
};

type NavGroup = {
  id: string;
  label: string;
  icon?: React.ReactNode;
  items?: NavItem[];
};

export type SidePanelUser = {
  name: string;
  email: string;
};

type SidePanelProps = {
  open: boolean;
  onClose: () => void;
  user: SidePanelUser;
  onNavigate?: (key: string) => void; // you can pass dashboardId here
};

function getInitials(nameOrEmail: string) {
  const s = (nameOrEmail || "").trim();
  if (!s) return "U";
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return "U";
}

const DashboardsIcon = React.memo(function DashboardsIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="sp-icon">
      <path
        fill="currentColor"
        d="M4 4h7v7H4Zm9 0h7v7h-7ZM4 13h7v7H4Zm9 0h7v7h-7Z"
      />
    </svg>
  );
});

export default function SidePanel({
  open,
  onClose,
  user,
  onNavigate,
}: SidePanelProps) {
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({
    myDashboards: true,
  });

  const location = useLocation();
  const navigate = useNavigate();
  const { dashboards } = useAuth();

  const goToDashboard = useCallback(
    (d: NavItem) => {
      navigate(`/dashboard/${d.id}/${d.workspaceId}`);
    },
    [navigate],
  );

  // Only recompute when pathname changes
  const activeDashboardId = React.useMemo(() => {
    const parts = location.pathname.split("/");
    return parts.length >= 3 && parts[1] === "dashboard" ? parts[2] : null;
  }, [location.pathname]);

  const initials = React.useMemo(
    () => getInitials(user.name || user.email),
    [user.name, user.email],
  );

  const handleItemClick = useCallback(
    (it: NavItem) => {
      goToDashboard(it);
      onClose();
    },
    [goToDashboard, onClose],
  );

  const toggleGroup = React.useCallback((groupId: string) => {
    setExpanded((p) => ({ ...p, [groupId]: !p[groupId] }));
  }, []);

  // close on ESC (only while open)
  React.useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  // prevent background scroll when open
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const dashboardItems: NavItem[] = React.useMemo(() => {
    return (dashboards ?? []).map((d: any) => ({
      id: d.id,
      label: d.name,
      workspaceId: d.workspaceId,
    }));
  }, [dashboards]);

  const groups: NavGroup[] = React.useMemo(
    () => [
      {
        id: "myDashboards",
        label: "My Dashboards",
        icon: <DashboardsIcon />,
        items: dashboardItems,
      },
    ],
    [dashboardItems],
  );

  return (
    <>
      <div
        className={`sp-backdrop ${open ? "sp-backdrop--open" : ""}`}
        onClick={onClose}
        aria-hidden={!open}
      />

      <aside
        className={`sp-drawer ${open ? "sp-drawer--open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Side panel"
      >
        <div className="sp-top">
          <div className="sp-user">
            <div className="sp-avatar" aria-hidden="true">
              {initials}
            </div>
            <div className="sp-user-meta">
              <div className="sp-user-name">{user.name || "User"}</div>
              <div className="sp-user-email">{user.email}</div>
            </div>
          </div>
        </div>

        <nav className="sp-nav" aria-label="Navigation">
          {groups.map((g) => {
            const hasChildren = !!g.items?.length;
            const isOpen = !!expanded[g.id];

            return (
              <div key={g.id} className="sp-section">
                <button
                  type="button"
                  className="sp-row"
                  onClick={() => toggleGroup(g.id)}
                  aria-expanded={hasChildren ? isOpen : undefined}
                >
                  <span className="sp-row-left">
                    {g.icon}
                    <span className="sp-label">{g.label}</span>
                  </span>

                  <span
                    className={`sp-chevron ${isOpen ? "sp-chevron--open" : ""}`}
                    aria-hidden="true"
                  >
                    <svg viewBox="0 0 24 24" className="sp-icon">
                      <path fill="currentColor" d="M7 10l5 5 5-5z" />
                    </svg>
                  </span>
                </button>

                <div className={`sp-sub ${isOpen ? "sp-sub--open" : ""}`}>
                  {!hasChildren ? (
                    <div
                      className="sp-empty"
                      style={{
                        padding: "8px 12px",
                        fontSize: 13,
                        opacity: 0.8,
                      }}
                    >
                      No dashboards available
                    </div>
                  ) : (
                    g.items!.map((it) => {
                      const isActive = it.id === activeDashboardId;

                      return (
                        <button
                          key={it.id}
                          type="button"
                          className={`sp-subitem ${isActive ? "sp-subitem--active" : ""}`}
                          onClick={() => handleItemClick(it)}
                        >
                          {it.label}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </nav>
      </aside>
    </>
  );
}
